import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { InputField } from '../components/InputField';
import { supabase } from '../lib/supabase';
import './RequestServicePage.css';

interface ServiceRequest {
  service_type: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_year: number | null;
  license_plate: string;
  location: {
    address: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  } | null;
}

const serviceTypes = [
  { id: 'jump-start', name: 'Jump Start', icon: 'fas fa-bolt' },
  { id: 'tire-change', name: 'Tire Change', icon: 'fas fa-car' },
  { id: 'fuel-delivery', name: 'Fuel Delivery', icon: 'fas fa-gas-pump' },
  { id: 'lockout', name: 'Lockout Service', icon: 'fas fa-key' },
];

export const RequestServicePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [request, setRequest] = useState<ServiceRequest>({
    service_type: searchParams.get('service') || '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: null,
    license_plate: '',
    location: null,
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleInputChange = (field: keyof ServiceRequest, value: any) => {
    setRequest(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Try multiple geocoding services for better address details
          let address = '';
          
          // First try: OpenStreetMap Nominatim (often better for street addresses)
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&zoom=18&extratags=1`
            );
            
            if (response.ok) {
              const data = await response.json();
              const addr = data.address;
              
              if (addr) {
                const addressParts = [];
                
                // Build address from OpenStreetMap data with better logic
                if (addr.house_number && addr.road) {
                  addressParts.push(`${addr.house_number} ${addr.road}`);
                } else if (addr.road) {
                  addressParts.push(addr.road);
                }
                
                // Add neighborhood/suburb (but avoid duplicates)
                if (addr.suburb && addr.suburb !== addr.city) {
                  addressParts.push(addr.suburb);
                } else if (addr.neighbourhood && addr.neighbourhood !== addr.city) {
                  addressParts.push(addr.neighbourhood);
                }
                
                // Add city (avoid duplicates)
                if (addr.city && !addressParts.includes(addr.city)) {
                  addressParts.push(addr.city);
                } else if (addr.town && !addressParts.includes(addr.town)) {
                  addressParts.push(addr.town);
                } else if (addr.village && !addressParts.includes(addr.village)) {
                  addressParts.push(addr.village);
                }
                
                // Add state (avoid duplicates)
                if (addr.state && !addressParts.includes(addr.state)) {
                  addressParts.push(addr.state);
                }
                
                // Add postal code
                if (addr.postcode) {
                  addressParts.push(addr.postcode);
                }
                
                // Add country (clean up the name)
                if (addr.country) {
                  const country = addr.country.replace(/\(the\)/gi, '').trim();
                  addressParts.push(country);
                }
                
                address = addressParts
                  .filter(part => part && part.trim())
                  .join(', ')
                  .replace(/^,\s*|,\s*$/g, '');
              }
            }
          } catch (error) {
            console.log('OpenStreetMap geocoding failed:', error);
          }
          
          // If we don't have a good street address, try BigDataCloud as fallback
          if (!address || (!address.includes('Street') && !address.includes('Avenue') && !address.includes('Road') && !address.includes('Drive') && !address.includes('Lane'))) {
            try {
              const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
              );
              
              if (response.ok) {
                const data = await response.json();
                
                const addressParts = [];
                
                // Add street address if available
                if (data.streetNumber && data.streetName) {
                  addressParts.push(`${data.streetNumber} ${data.streetName}`);
                } else if (data.streetName) {
                  addressParts.push(data.streetName);
                }
                
                // Add city (avoid duplicates)
                if (data.city && !addressParts.includes(data.city)) {
                  addressParts.push(data.city);
                }
                
                // Add state (avoid duplicates)
                if (data.principalSubdivision && !addressParts.includes(data.principalSubdivision)) {
                  addressParts.push(data.principalSubdivision);
                }
                
                // Add postal code
                if (data.postcode) {
                  addressParts.push(data.postcode);
                }
                
                // Add country (clean up)
                if (data.countryName) {
                  const country = data.countryName.replace(/\(the\)/gi, '').trim();
                  addressParts.push(country);
                }
                
                const bigDataAddress = addressParts
                  .filter(part => part && part.trim())
                  .join(', ')
                  .replace(/^,\s*|,\s*$/g, '');
                
                // Use this address if it's better (has street info or is more detailed)
                if (bigDataAddress && (bigDataAddress.includes('Street') || bigDataAddress.includes('Avenue') || bigDataAddress.includes('Road') || bigDataAddress.length > address.length)) {
                  address = bigDataAddress;
                }
              }
            } catch (error) {
              console.log('BigDataCloud geocoding failed:', error);
            }
          }
          
          // If we still don't have a good address, use coordinates
          if (!address || address.split(',').length < 2) {
            address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          }
          
          setRequest(prev => ({
            ...prev,
            location: {
              address: address,
              coordinates: {
                lat: latitude,
                lng: longitude
              }
            }
          }));
        } catch (error) {
          console.error('Error getting address from coordinates:', error);
          // Fallback to coordinates
          setRequest(prev => ({
            ...prev,
            location: {
              address: `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`,
              coordinates: {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              }
            }
          }));
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        setLocationLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert('Location access denied. Please enable location permissions or enter your address manually.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information is unavailable. Please enter your address manually.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out. Please enter your address manually.');
            break;
          default:
            alert('An unknown error occurred while getting your location. Please enter your address manually.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data for database schema
      const serviceRequestData = {
        customer_id: user?.id, // Will be set by backend based on authenticated user
        service_type: request.service_type,
        vehicle_make: request.vehicle_make,
        vehicle_model: request.vehicle_model,
        vehicle_year: request.vehicle_year,
        license_plate: request.license_plate,
        location: request.location,
        // status will default to 'pending' in database
        // created_at and updated_at will be set automatically
      };

      console.log('Service Request Data:', serviceRequestData);
      
      // Submit service request to Supabase
      const { data, error } = await supabase
        .from('service_requests')
        .insert([serviceRequestData])
        .select();
      
      if (error) {
        console.error('Supabase error:', error);
        throw new Error(`Failed to submit service request: ${error.message}`);
      }
      
      console.log('Service request submitted successfully:', data);
      
      // Navigate to tracking page after successful submission
      navigate('/tracking');
    } catch (error) {
      console.error('Error submitting service request:', error);
      alert('Failed to submit service request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedService = serviceTypes.find(s => s.id === request.service_type);

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className="request-service-page">
      <div className="request-container">
        <header className="request-header">
          <h1>Request Roadside Assistance</h1>
          {selectedService && (
            <div className="selected-service">
              <i className={selectedService.icon}></i>
              <span>{selectedService.name}</span>
            </div>
          )}
        </header>

        <form onSubmit={handleSubmit} className="request-form">
          <div className="form-section">
            <h3>Service Details</h3>
            <div className="service-types">
              {serviceTypes.map(service => (
                <button
                  key={service.id}
                  type="button"
                  className={`service-type-btn ${request.service_type === service.id ? 'selected' : ''}`}
                  onClick={() => handleInputChange('service_type', service.id)}
                >
                  <i className={service.icon}></i>
                  <span>{service.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>Vehicle Information</h3>
            <div className="form-row">
              <InputField
                label="Make"
                value={request.vehicle_make}
                onChangeText={(value: string) => handleInputChange('vehicle_make', value)}
                placeholder="e.g., Toyota"
              />
              <InputField
                label="Model"
                value={request.vehicle_model}
                onChangeText={(value: string) => handleInputChange('vehicle_model', value)}
                placeholder="e.g., Camry"
              />
            </div>
            <div className="form-row">
              <InputField
                label="Year"
                value={request.vehicle_year?.toString() || ''}
                onChangeText={(value: string) => handleInputChange('vehicle_year', value ? parseInt(value) : null)}
                placeholder="e.g., 2020"
              />
              <InputField
                label="License Plate"
                value={request.license_plate}
                onChangeText={(value: string) => handleInputChange('license_plate', value)}
                placeholder="e.g., ABC123"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Location & Details</h3>
            <div className="location-field">
              <InputField
                label="Current Location"
                value={request.location?.address || ''}
                onChangeText={(value: string) => handleInputChange('location', { address: value })}
                placeholder="Enter your current address or location"
              />
              <button
                type="button"
                className="location-btn"
                onClick={getCurrentLocation}
                disabled={locationLoading}
              >
                {locationLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Getting Location...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-map-marker-alt"></i>
                    <span>Use Current Location</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="form-actions">
            <Button
              title="Cancel"
              onPress={() => navigate('/')}
              variant="outline"
              style={{ marginRight: '1rem' }}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !request.service_type || !request.vehicle_make || !request.vehicle_model || !request.vehicle_year || !request.license_plate || !request.location?.address}
            >
              {loading ? "Submitting..." : "Request Service"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
